import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Avatar,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import ImageIcon from '@mui/icons-material/Image';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import LinkIcon from '@mui/icons-material/Link';
import TuneIcon from '@mui/icons-material/Tune'; // ✅ Yeni: Özelleştirme ikonu
import SaveIcon from '@mui/icons-material/Save'; // ✅ Yeni: Taslak kaydetme ikonu
import { KioskCategory } from './KioskCategoriesTab';
import { accountingApi } from '../../../config/apiClient';
import { useTranslation } from 'react-i18next';

// Kiosk Ürün veri tipi (sadece ID referansı)
export interface KioskProduct {
  kioskProductId: string; // ✅ YENİ: InventoryItems.Id (Guid) - artık int değil
  order: number;
  relatedProductIds?: string[]; // ✅ YENİ: Guid dizisi - Bu ürünle birlikte gidebilecek ürünlerin ID'leri
  customizationOptions?: string[]; // ✅ Ürün özelleştirme seçenekleri (örn: ["Ekstra Peynir", "Ketçap", "Mayonez"])
}

// ✅ YENİ: InventoryItems tablosundan gelen ürün detayları (IsKiosk=true)
interface KioskProductDetail {
  id: string; // ✅ Guid
  companyId: string;
  name: string;
  stockCode?: string;
  barcode?: string;
  categoryId?: string;
  categoryName?: string;
  brandId?: string;
  brandName?: string;
  salePrice: number; // ✅ Şube fiyatı varsa şube fiyatı, yoksa ana fiyat
  saleCurrency?: string;
  originalSalePrice?: number; // ✅ YENİ: Ana fiyat (şube fiyatı ile karşılaştırma için)
  useBranchPricing?: boolean; // ✅ YENİ: Şube fiyatlandırması aktif mi
  usedBranchPrice?: boolean; // ✅ YENİ: Şube fiyatı kullanıldı mı
  description?: string;
  imageUrl?: string;
  isActive: boolean;
  isKiosk: boolean; // ✅ Her zaman true olacak (filtrelenmiş)
}

interface KioskProductsTabProps {
  categories: KioskCategory[];
  products: { [categoryId: string]: KioskProduct[] };
  onProductsChange: (products: { [categoryId: string]: KioskProduct[] }) => void;
  onCategoriesChange: (categories: KioskCategory[]) => void; // ✅ Yeni: Kategori ayarlarını güncellemek için
  companyId: string | null;
  branchId?: string | null; // ✅ YENİ: Şube fiyatlarını çekmek için (opsiyonel)
  onOpenImageLibrary?: (callback: (imageUrl: string) => void) => void; // ✅ Resim kütüphanesi callback'i
}

const KioskProductsTab: React.FC<KioskProductsTabProps> = ({
  categories,
  products,
  onProductsChange,
  branchId,
  onCategoriesChange, // ✅ Yeni prop
  companyId,
  onOpenImageLibrary // ✅ Resim kütüphanesi callback'i
}) => {
  const { t, i18n } = useTranslation('salesSystem');
  const numberLocale = i18n.language?.startsWith('tr') ? 'tr-TR' : 'en-US';
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1499px)');
  
  // Local state'ler
  const [activeCategoryTab, setActiveCategoryTab] = useState<number>(0);
  const [productDialogOpen, setProductDialogOpen] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid'); // Görünüm modu
  
  // Ürün seçimi için state'ler
  const [availableProducts, setAvailableProducts] = useState<KioskProductDetail[]>([]);
  const [filteredAvailableProducts, setFilteredAvailableProducts] = useState<KioskProductDetail[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]); // ✅ YENİ: Guid dizisi
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Ek ürün seçimi için state'ler
  const [relatedProductDialogOpen, setRelatedProductDialogOpen] = useState<boolean>(false);
  const [currentProductForRelated, setCurrentProductForRelated] = useState<{ categoryId: string; kioskProductId: string } | null>(null); // ✅ YENİ: string
  const [selectedRelatedProductIds, setSelectedRelatedProductIds] = useState<string[]>([]); // ✅ YENİ: Guid dizisi

  // ✅ Yeni: Ürün özelleştirme seçenekleri için state'ler
  const [customizationDialogOpen, setCustomizationDialogOpen] = useState<boolean>(false);
  const [currentProductForCustomization, setCurrentProductForCustomization] = useState<{ categoryId: string; kioskProductId: string } | null>(null); // ✅ YENİ: string
  const [customizationOptions, setCustomizationOptions] = useState<string[]>([]);
  const [newOptionDialogOpen, setNewOptionDialogOpen] = useState<boolean>(false);
  const [newOptionText, setNewOptionText] = useState<string>('');
  
  // ✅ Yeni: Taslak yönetimi için state'ler
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState<boolean>(false);
  const [templateName, setTemplateName] = useState<string>('');
  const [savedTemplates, setSavedTemplates] = useState<{ name: string; options: string[] }[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  // Kategori değiştiğinde ilk kategoriyi seç
  useEffect(() => {
    if (categories.length > 0 && activeCategoryTab >= categories.length) {
      setActiveCategoryTab(0);
    }
  }, [categories, activeCategoryTab]);
  
  // ✅ Yeni: Taslakları localStorage'dan yükle
  useEffect(() => {
    const loadTemplates = () => {
      try {
        const stored = localStorage.getItem('kioskCustomizationTemplates');
        if (stored) {
          const templates = JSON.parse(stored);
          setSavedTemplates(templates);
          console.log('✅ [KIOSK CUSTOMIZATION] Taslaklar yüklendi:', templates.length);
        }
      } catch (error) {
        console.error('❌ [KIOSK CUSTOMIZATION] Taslak yükleme hatası:', error);
      }
    };
    
    loadTemplates();
  }, []);
  
  // ✅ Yeni: Taslağı localStorage'a kaydet
  const saveTemplate = (name: string, options: string[]) => {
    try {
      const newTemplate = { name, options };
      const updatedTemplates = [...savedTemplates, newTemplate];
      localStorage.setItem('kioskCustomizationTemplates', JSON.stringify(updatedTemplates));
      setSavedTemplates(updatedTemplates);
      console.log('✅ [KIOSK CUSTOMIZATION] Taslak kaydedildi:', name);
      return true;
    } catch (error) {
      console.error('❌ [KIOSK CUSTOMIZATION] Taslak kaydetme hatası:', error);
      return false;
    }
  };
  
  // ✅ Yeni: Taslağı sil
  const deleteTemplate = (templateName: string) => {
    try {
      const updatedTemplates = savedTemplates.filter(t => t.name !== templateName);
      localStorage.setItem('kioskCustomizationTemplates', JSON.stringify(updatedTemplates));
      setSavedTemplates(updatedTemplates);
      console.log('✅ [KIOSK CUSTOMIZATION] Taslak silindi:', templateName);
    } catch (error) {
      console.error('❌ [KIOSK CUSTOMIZATION] Taslak silme hatası:', error);
    }
  };
  
  // ✅ Yeni: Taslağı uygula
  const applyTemplate = (templateName: string) => {
    const template = savedTemplates.find(t => t.name === templateName);
    if (template) {
      setCustomizationOptions([...template.options]);
      setSelectedTemplate(templateName);
      console.log('✅ [KIOSK CUSTOMIZATION] Taslak uygulandı:', templateName, template.options);
    }
  };

  // Aktif kategori
  const activeCategory = categories[activeCategoryTab];
  const categoryProducts = activeCategory ? (products[activeCategory.id] || []) : [];

  // ✅ Yeni: Kategorideki herhangi bir üründe alt ürün var mı kontrol et
  const categoryHasRelatedProducts = categoryProducts.some(p => p.relatedProductIds && p.relatedProductIds.length > 0);

  // ✅ Yeni: Kategori için "Ek Ürünleri Tüm Ürünlerde Göster" ayarını toggle et
  const handleToggleShowRelatedForAll = (categoryId: string, value: boolean) => {
    const updatedCategories = categories.map(cat =>
      cat.id === categoryId
        ? { ...cat, showRelatedProductsForAll: value }
        : cat
    );
    onCategoriesChange(updatedCategories);
    console.log('✅ [KIOSK PRODUCTS] Kategori "Tüm Ürünlerde Ek Ürün" ayarı güncellendi:', {
      categoryId,
      showRelatedProductsForAll: value
    });
  };

  // ✅ YENİ: InventoryItems tablosundan ürünleri yükle (IsKiosk=true)
  // ✅ branchId varsa şube fiyatlarıyla birlikte çeker
  const loadAvailableProducts = async () => {
    if (!companyId) return;

    setLoadingProducts(true);
    try {
      console.log('🔍 [KIOSK PRODUCTS TAB] InventoryItems (IsKiosk=true) yükleniyor...', { companyId, branchId });

      const branchParam = branchId ? `?branchId=${encodeURIComponent(branchId)}` : '';
      const response = await accountingApi.get(`/api/Inventory/kiosk-items${branchParam}`);

      const result = response.data;
      if (result.success && result.data && Array.isArray(result.data)) {
        const activeProducts = result.data
          .filter((p: any) => p.isActive && p.isKiosk)
          .map((item: any) => ({
            id: item.id,
            companyId: item.companyId,
            name: item.name || 'İsimsiz Ürün',
            stockCode: item.stockCode || '-',
            barcode: item.barcode || '-',
            categoryId: item.categoryId || null,
            categoryName: item.categoryName || '-',
            brandId: item.brandId || null,
            brandName: item.brandName || '-',
            salePrice: item.salePrice || 0,
            saleCurrency: item.saleCurrency || 'TRY',
            originalSalePrice: item.originalSalePrice ?? item.salePrice ?? 0,
            useBranchPricing: item.useBranchPricing ?? false,
            usedBranchPrice: item.usedBranchPrice ?? false,
            description: item.description || '',
            imageUrl: item.imageUrl || null,
            isActive: item.isActive,
            isKiosk: item.isKiosk
          }));

        setAvailableProducts(activeProducts);
        console.log('✅ [KIOSK PRODUCTS TAB] InventoryItems yüklendi (IsKiosk=true)', { count: activeProducts.length });
      } else {
        setAvailableProducts([]);
        console.warn('⚠️ [KIOSK PRODUCTS TAB] API\'den veri alınamadı');
      }
    } catch (error) {
      console.error('❌ [KIOSK PRODUCTS TAB] Ürünler yüklenirken hata:', error);
      setError(t('kioskProductsTab.errors.productsLoadFailed'));
    } finally {
      setLoadingProducts(false);
    }
  };

  // Modal açıldığında ürünleri yükle
  useEffect(() => {
    if (productDialogOpen && companyId) {
      loadAvailableProducts();
    }
  }, [productDialogOpen, companyId]);

  // Component mount olduğunda veya companyId değiştiğinde ürünleri yükle (gösterim için)
  useEffect(() => {
    if (companyId) {
      loadAvailableProducts();
    }
  }, [companyId]);

  // Arama ve filtreleme
  useEffect(() => {
    let filtered = [...availableProducts];

    // Sadece aktif kategoriye eklenmiş ürünleri filtrele (diğer kategorilerde olabilir)
    const currentCategoryProductIds = activeCategory 
      ? (products[activeCategory.id] || []).map(p => p.kioskProductId)
      : [];
    filtered = filtered.filter(p => !currentCategoryProductIds.includes(p.id));

    // Arama filtresi
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (product) =>
          product.name.toLowerCase().includes(lowerSearch) ||
          product.stockCode?.toLowerCase().includes(lowerSearch) ||
          product.barcode?.toLowerCase().includes(lowerSearch)
      );
    }

    // Kategori filtresi
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(p => p.categoryName === categoryFilter);
    }

    setFilteredAvailableProducts(filtered);
    setPage(0);
  }, [availableProducts, searchTerm, categoryFilter, products, activeCategory]);

  // Benzersiz kategorileri al
  const uniqueCategories = Array.from(new Set(availableProducts.map(p => p.categoryName).filter(Boolean))) as string[];

  // Ürün seçimini toggle et
  const handleToggleProduct = (productId: string) => { // ✅ YENİ: string (Guid)
    setSelectedProductIds(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Tümünü seç/kaldır
  const handleSelectAll = () => {
    const currentPageProductIds = paginatedProducts.map(p => p.id);
    const allSelected = currentPageProductIds.every(id => selectedProductIds.includes(id));
    
    if (allSelected) {
      // Tümünü kaldır
      setSelectedProductIds(prev => prev.filter(id => !currentPageProductIds.includes(id)));
    } else {
      // Tümünü seç
      const combined = [...selectedProductIds, ...currentPageProductIds];
      const unique = combined.filter((id, index) => combined.indexOf(id) === index);
      setSelectedProductIds(unique);
    }
  };

  // Seçili ürünleri kategoriye ekle
  const handleAddSelectedProducts = () => {
    if (!activeCategory || selectedProductIds.length === 0) return;

    const updatedProducts = { ...products };
    const currentCategoryProducts = updatedProducts[activeCategory.id] || [];
    
    // Yeni ürünleri ekle
    const newProducts: KioskProduct[] = selectedProductIds.map((productId, index) => ({
      kioskProductId: productId,
      order: currentCategoryProducts.length + index + 1
    }));

    updatedProducts[activeCategory.id] = [...currentCategoryProducts, ...newProducts];
    onProductsChange(updatedProducts);

    // Seçimleri temizle ve modalı kapat
    setSelectedProductIds([]);
    setSearchTerm('');
    setCategoryFilter('all');
    setProductDialogOpen(false);

    console.log('✅ [KIOSK PRODUCTS TAB] Ürünler eklendi', { 
      categoryId: activeCategory.id, 
      count: newProducts.length 
    });
  };

  // Sayfalama
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedProducts = filteredAvailableProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Para birimi sembolü
  const getCurrencySymbol = (currency?: string): string => {
    switch (currency?.toUpperCase()) {
      case 'TRY':
      case 'TL':
        return '₺';
      case 'USD':
        return '$';
      case 'EUR':
        return '€';
      case 'GBP':
        return '£';
      default:
        return '₺';
    }
  };

  // Ürün detaylarını InventoryItems tablosundan al (gösterim için)
  const getProductDetails = (kioskProductId: string): KioskProductDetail | null => { // ✅ YENİ: string (Guid)
    return availableProducts.find(p => p.id === kioskProductId) || null;
  };

  return (
    <>
      <Paper elevation={0} sx={{ p: 2, minHeight: '500px' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{t('kioskProductsTab.title')}</Typography>
        </Box>

        {categories.length === 0 ? (
          // Kategori yoksa uyarı göster
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', bgcolor: 'rgba(0,0,0,0.02)' }}>
            <CategoryIcon sx={{ fontSize: '4rem', color: 'grey.300', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('kioskProductsTab.emptyState.noCategoryTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('kioskProductsTab.emptyState.noCategoryDescription')}
            </Typography>
            <Button 
              variant="outlined"
              startIcon={<CategoryIcon />}
              onClick={() => {
                // Ana modal'daki tab'ı değiştir (parent'a sinyal gönder)
                // Bu durumda kullanıcıyı yönlendirmek için bir mesaj gösterelim
              }}
            >
              {t('kioskProductsTab.emptyState.goToCategoriesTab')}
            </Button>
          </Paper>
        ) : (
          <>
            {/* Kategori Sekmeleri */}
            {/* 💻 MASAÜSTÜ: Normal Tabs */}
            {!isMobile && (
              <Box sx={{ 
                borderBottom: 1, 
                borderColor: 'divider', 
                mt: 2, 
                mb: 2,
                display: 'flex',
                alignItems: 'center'
              }}>
                <Tabs
                  value={activeCategoryTab}
                  onChange={(e, newValue) => setActiveCategoryTab(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{ 
                    flexGrow: 1,
                    '.MuiTab-root': { 
                      textTransform: 'none', 
                      minHeight: 'auto',
                      py: 1
                    }
                  }}
                >
                  {categories.map((category, index) => {
                    // ✅ Her kategori için ayrı ürün sayısını hesapla
                    const categoryProductCount = (products[category.id] || []).length;
                    
                    return (
                      <Tab
                        key={category.id}
                        label={(
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {category.imageUrl && (
                              <Avatar 
                                src={category.imageUrl} 
                                alt={category.name}
                                sx={{ width: 24, height: 24 }}
                              />
                            )}
                            <Typography variant="body2">
                              {category.name}
                            </Typography>
                            <Chip 
                              label={categoryProductCount || 0} 
                              size="small" 
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Box>
                        )}
                      />
                    );
                  })}
                </Tabs>
              </Box>
            )}
            
            {/* 📱 MOBİL: Dropdown Select */}
            {isMobile && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="products-category-select-label">{t('kioskProductsTab.categorySelect')}</InputLabel>
                  <Select
                    labelId="products-category-select-label"
                    value={activeCategoryTab}
                    label={t('kioskProductsTab.categorySelect')}
                    onChange={(e) => setActiveCategoryTab(e.target.value as number)}
                  >
                    {categories.map((category, index) => {
                      const categoryProductCount = (products[category.id] || []).length;
                      
                      return (
                        <MenuItem key={category.id} value={index}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {category.imageUrl ? (
                                <Avatar src={category.imageUrl} sx={{ width: 24, height: 24 }} />
                              ) : (
                                <CategoryIcon sx={{ fontSize: '1.2rem', color: 'primary.main' }} />
                              )}
                              <Typography variant="body2">{category.name}</Typography>
                            </Box>
                            <Chip 
                              label={categoryProductCount} 
                              size="small" 
                              color="primary"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Kategori İçeriği - Ürünler */}
            {categories.map((category, index) => (
              <Box
                key={category.id}
                role="tabpanel"
                hidden={activeCategoryTab !== index}
                id={`category-products-tabpanel-${category.id}`}
                aria-labelledby={`category-products-tab-${category.id}`}
                sx={{ mt: 2 }}
              >
                {activeCategoryTab === index && (
                  <Box>
                    {/* Kategori Başlığı ve Ürün Ekleme Butonu */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {category.imageUrl && (
                          <Avatar 
                            src={category.imageUrl} 
                            alt={category.name}
                            variant="rounded"
                            sx={{ width: 60, height: 60 }}
                          />
                        )}
                        <Box>
                          <Typography variant="h6">
                            {category.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('kioskProductsTab.productCount', { count: categoryProducts.length })}
                          </Typography>
                        </Box>
                        
                        {/* ✅ Yeni: Ek Ürünleri Tüm Ürünlerde Göster Switch */}
                        {categoryHasRelatedProducts && (
                          <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={category.showRelatedProductsForAll || false}
                                  onChange={(e) => handleToggleShowRelatedForAll(category.id, e.target.checked)}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="caption" fontWeight="medium">
                                    {t('kioskProductsTab.relatedToggle.label')}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                                    {category.showRelatedProductsForAll 
                                      ? t('kioskProductsTab.relatedToggle.onDescription')
                                      : t('kioskProductsTab.relatedToggle.offDescription')}
                                  </Typography>
                                </Box>
                              }
                              sx={{ 
                                m: 0,
                                p: 1.5,
                                bgcolor: category.showRelatedProductsForAll ? 'success.50' : 'grey.50',
                                borderRadius: 1,
                                border: 1,
                                borderColor: category.showRelatedProductsForAll ? 'success.main' : 'divider'
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                      
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {/* Görünüm Toggle */}
                        <Box sx={{ display: 'flex', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                          <Tooltip title={t('kioskProductsTab.viewMode.grid')}>
                            <IconButton
                              size="small"
                              onClick={() => setViewMode('grid')}
                              sx={{
                                borderRadius: 0,
                                bgcolor: viewMode === 'grid' ? 'primary.main' : 'transparent',
                                color: viewMode === 'grid' ? 'white' : 'inherit',
                                '&:hover': {
                                  bgcolor: viewMode === 'grid' ? 'primary.dark' : 'action.hover'
                                }
                              }}
                            >
                              <ViewModuleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('kioskProductsTab.viewMode.table')}>
                            <IconButton
                              size="small"
                              onClick={() => setViewMode('table')}
                              sx={{
                                borderRadius: 0,
                                bgcolor: viewMode === 'table' ? 'primary.main' : 'transparent',
                                color: viewMode === 'table' ? 'white' : 'inherit',
                                '&:hover': {
                                  bgcolor: viewMode === 'table' ? 'primary.dark' : 'action.hover'
                                }
                              }}
                            >
                              <ViewListIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => setProductDialogOpen(true)}
                          size="small"
                        >
                          {t('kioskProductsTab.actions.addProduct')}
                        </Button>
                      </Box>
                    </Box>

                    {/* Ürün Listesi */}
                    {categoryProducts.length > 0 ? (
                      <>
                        {/* Kart Görünümü */}
                        {viewMode === 'grid' && (
                          <Grid container spacing={2}>
                            {categoryProducts.map((product) => {
                              // KioskProducts tablosundan ürün detaylarını al
                              const productDetail = availableProducts.find(p => p.id === product.kioskProductId);
                              
                              if (!productDetail) {
                                return null; // Ürün detayı bulunamadıysa gösterme
                              }

                              return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={product.kioskProductId}>
                                  <Card 
                                    variant="outlined"
                                    sx={{ 
                                      height: '100%',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        boxShadow: 3,
                                        transform: 'translateY(-4px)'
                                      }
                                    }}
                                  >
                                    {/* Ürün Resmi */}
                                    {productDetail.imageUrl ? (
                                      <CardMedia
                                        component="img"
                                        height="160"
                                        image={productDetail.imageUrl}
                                        alt={productDetail.name}
                                        sx={{ objectFit: 'cover' }}
                                      />
                                    ) : (
                                      <Box
                                        sx={{
                                          height: 160,
                                          bgcolor: 'grey.100',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center'
                                        }}
                                      >
                                        <ShoppingCartIcon sx={{ fontSize: '4rem', color: 'grey.300' }} />
                                      </Box>
                                    )}

                                    {/* Ürün Bilgileri */}
                                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                      <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                                        {productDetail.name}
                                      </Typography>
                                      
                                      {productDetail.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, flexGrow: 1 }}>
                                          {productDetail.description.length > 60 
                                            ? `${productDetail.description.substring(0, 60)}...` 
                                            : productDetail.description}
                                        </Typography>
                                      )}

                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                                        <Box>
                                          <Typography variant="h6" color="primary">
                                            {productDetail.salePrice} {getCurrencySymbol(productDetail.saleCurrency)}
                                          </Typography>
                                          {productDetail.usedBranchPrice && productDetail.originalSalePrice !== undefined && productDetail.originalSalePrice !== productDetail.salePrice && (
                                            <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                              {t('kioskProductsTab.priceLabels.main')}: {productDetail.originalSalePrice} {getCurrencySymbol(productDetail.saleCurrency)}
                                            </Typography>
                                          )}
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                          <Tooltip title={`${t('kioskProductsTab.tooltips.relatedProducts')} ${product.relatedProductIds?.length ? `(${product.relatedProductIds.length})` : ''}`}>
                                            <IconButton
                                              size="small"
                                              color={product.relatedProductIds?.length ? 'success' : 'default'}
                                              onClick={() => {
                                                setCurrentProductForRelated({ categoryId: category.id, kioskProductId: product.kioskProductId });
                                                setSelectedRelatedProductIds(product.relatedProductIds || []);
                                                setRelatedProductDialogOpen(true);
                                              }}
                                            >
                                              <LinkIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title={`${t('kioskProductsTab.tooltips.customizationOptions')} ${product.customizationOptions?.length ? `(${product.customizationOptions.length})` : ''}`}>
                                            <IconButton
                                              size="small"
                                              color={product.customizationOptions?.length ? 'primary' : 'default'}
                                              onClick={() => {
                                                setCurrentProductForCustomization({ categoryId: category.id, kioskProductId: product.kioskProductId });
                                                setCustomizationOptions(product.customizationOptions || []);
                                                setSelectedTemplate(''); // ✅ Taslak seçimini sıfırla
                                                setCustomizationDialogOpen(true);
                                              }}
                                            >
                                              <TuneIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title={t('kioskProductsTab.tooltips.removeProduct')}>
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() => {
                                                const updatedProducts = { ...products };
                                                updatedProducts[category.id] = categoryProducts.filter(p => p.kioskProductId !== product.kioskProductId);
                                                onProductsChange(updatedProducts);
                                              }}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      </Box>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              );
                            })}
                          </Grid>
                        )}

                        {/* Tablo Görünümü */}
                        {viewMode === 'table' && (
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>{t('kioskProductsTab.tableHeaders.product')}</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>{t('kioskProductsTab.tableHeaders.stockCode')}</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>{t('kioskProductsTab.tableHeaders.category')}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>{t('kioskProductsTab.tableHeaders.price')}</TableCell>
                                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>{t('kioskProductsTab.tableHeaders.quantity')}</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'grey.50' }}>{t('kioskProductsTab.tableHeaders.actions')}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {categoryProducts.map((product) => {
                                  const productDetail = availableProducts.find(p => p.id === product.kioskProductId);
                                  
                                  if (!productDetail) {
                                    return null;
                                  }

                                  return (
                                    <TableRow 
                                      key={product.kioskProductId}
                                      hover
                                      sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                                    >
                                      <TableCell>
                                        <Box display="flex" alignItems="center" gap={1.5}>
                                          {productDetail.imageUrl ? (
                                            <Avatar
                                              src={productDetail.imageUrl}
                                              alt={productDetail.name}
                                              variant="rounded"
                                              sx={{ width: 50, height: 50 }}
                                            />
                                          ) : (
                                            <Avatar variant="rounded" sx={{ width: 50, height: 50, bgcolor: 'grey.300' }}>
                                              <ImageIcon />
                                            </Avatar>
                                          )}
                                          <Box>
                                            <Typography variant="body2" fontWeight="medium">
                                              {productDetail.name}
                                            </Typography>
                                            {productDetail.description && (
                                              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                                                {productDetail.description}
                                              </Typography>
                                            )}
                                          </Box>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="body2">{productDetail.stockCode || '-'}</Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Chip 
                                          label={productDetail.categoryName || '-'} 
                                          size="small" 
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" fontWeight="bold" color="success.main">
                                          {productDetail.salePrice.toLocaleString(numberLocale, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}{' '}
                                          {getCurrencySymbol(productDetail.saleCurrency)}
                                          {productDetail.usedBranchPrice && (
                                            <Chip label={t('kioskProductsTab.priceLabels.branch')} size="small" color="info" variant="outlined" sx={{ ml: 0.5, fontSize: '0.65rem', height: 18 }} />
                                          )}
                                        </Typography>
                                        {productDetail.usedBranchPrice && productDetail.originalSalePrice !== undefined && productDetail.originalSalePrice !== productDetail.salePrice && (
                                          <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                            {t('kioskProductsTab.priceLabels.main')}: {productDetail.originalSalePrice.toLocaleString(numberLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getCurrencySymbol(productDetail.saleCurrency)}
                                          </Typography>
                                        )}
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" color="text.secondary">
                                          {/* ✅ YENİ: InventoryItems'da quantity/unit yok, stok bilgisi WarehouseStock'tan gelir */}
                                          -
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="center">
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                          <Tooltip title={`${t('kioskProductsTab.tooltips.relatedProducts')} ${product.relatedProductIds?.length ? `(${product.relatedProductIds.length})` : ''}`}>
                                            <IconButton
                                              size="small"
                                              color={product.relatedProductIds?.length ? 'success' : 'default'}
                                              onClick={() => {
                                                setCurrentProductForRelated({ categoryId: category.id, kioskProductId: product.kioskProductId });
                                                setSelectedRelatedProductIds(product.relatedProductIds || []);
                                                setRelatedProductDialogOpen(true);
                                              }}
                                            >
                                              <LinkIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title={`${t('kioskProductsTab.tooltips.customizationOptions')} ${product.customizationOptions?.length ? `(${product.customizationOptions.length})` : ''}`}>
                                            <IconButton
                                              size="small"
                                              color={product.customizationOptions?.length ? 'primary' : 'default'}
                                              onClick={() => {
                                                setCurrentProductForCustomization({ categoryId: category.id, kioskProductId: product.kioskProductId });
                                                setCustomizationOptions(product.customizationOptions || []);
                                                setSelectedTemplate(''); // ✅ Taslak seçimini sıfırla
                                                setCustomizationDialogOpen(true);
                                              }}
                                            >
                                              <TuneIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title={t('kioskProductsTab.tooltips.removeProduct')}>
                                            <IconButton
                                              size="small"
                                              color="error"
                                              onClick={() => {
                                                const updatedProducts = { ...products };
                                                updatedProducts[category.id] = categoryProducts.filter(p => p.kioskProductId !== product.kioskProductId);
                                                onProductsChange(updatedProducts);
                                              }}
                                            >
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </>
                    ) : (
                      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', bgcolor: 'rgba(0,0,0,0.02)' }}>
                        <ShoppingCartIcon sx={{ fontSize: '4rem', color: 'grey.300', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          {t('kioskProductsTab.emptyState.noProductsInCategoryTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          {t('kioskProductsTab.emptyState.noProductsInCategoryDescription', { categoryName: category.name })}
                        </Typography>
                        <Button 
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => setProductDialogOpen(true)}
                        >
                          {t('kioskProductsTab.actions.addFirstProduct')}
                        </Button>
                      </Paper>
                    )}
                  </Box>
                )}
              </Box>
            ))}
          </>
        )}
      </Paper>

      {/* Ürün Ekleme Dialog */}
      <Dialog 
        open={productDialogOpen} 
        onClose={() => {
          setProductDialogOpen(false);
          setSelectedProductIds([]);
          setSearchTerm('');
          setCategoryFilter('all');
        }} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">
                {t('kioskProductsTab.dialogs.addProduct.title', { categoryName: activeCategory?.name || '' })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedProductIds.length > 0 && t('kioskProductsTab.dialogs.addProduct.selectedCount', { count: selectedProductIds.length })}
              </Typography>
            </Box>
            <IconButton
              aria-label={t('kioskProductsTab.actions.close')}
              onClick={() => {
                setProductDialogOpen(false);
                setSelectedProductIds([]);
                setSearchTerm('');
                setCategoryFilter('all');
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingProducts ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Arama ve Filtreler */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
                  placeholder={t('kioskProductsTab.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{ flexGrow: 1, minWidth: 250 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>{t('kioskProductsTab.categoryFilter')}</InputLabel>
                  <Select
                    value={categoryFilter}
                    label={t('kioskProductsTab.categoryFilter')}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <MenuItem value="all">{t('kioskProductsTab.allCategories')}</MenuItem>
                    {uniqueCategories.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* İstatistikler */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Paper variant="outlined" sx={{ p: 1.5, flexGrow: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('kioskProductsTab.stats.totalProducts')}
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {availableProducts.length}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5, flexGrow: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('kioskProductsTab.stats.filtered')}
                  </Typography>
                  <Typography variant="h6" color="info.main">
                    {filteredAvailableProducts.length}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.5, flexGrow: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('kioskProductsTab.stats.selected')}
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {selectedProductIds.length}
                  </Typography>
                </Paper>
              </Box>

              {/* Ürün Tablosu */}
              {filteredAvailableProducts.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {searchTerm || categoryFilter !== 'all' 
                    ? t('kioskProductsTab.emptyState.noSearchResults')
                    : t('kioskProductsTab.emptyState.noAvailableProducts')}
                </Alert>
              ) : (
                <>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 450 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={
                                selectedProductIds.length > 0 &&
                                selectedProductIds.length < paginatedProducts.length
                              }
                              checked={
                                paginatedProducts.length > 0 &&
                                paginatedProducts.every(p => selectedProductIds.includes(p.id))
                              }
                              onChange={handleSelectAll}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('kioskProductsTab.tableHeaders.product')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('kioskProductsTab.tableHeaders.category')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('kioskProductsTab.tableHeaders.stockCode')}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('kioskProductsTab.tableHeaders.price')}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('kioskProductsTab.tableHeaders.quantity')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedProducts.map((product) => (
                          <TableRow
                            key={product.id}
                            hover
                            onClick={() => handleToggleProduct(product.id)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedProductIds.includes(product.id)}
                                onChange={() => handleToggleProduct(product.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                {product.imageUrl ? (
                                  <Avatar
                                    src={product.imageUrl}
                                    alt={product.name}
                                    variant="rounded"
                                    sx={{ width: 40, height: 40 }}
                                  />
                                ) : (
                                  <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: 'grey.300' }}>
                                    <ImageIcon />
                                  </Avatar>
                                )}
                                <Typography variant="body2">{product.name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{product.categoryName || '-'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{product.stockCode || '-'}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight="medium" color="success.main">
                                {product.salePrice.toLocaleString(numberLocale, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{' '}
                                {getCurrencySymbol(product.saleCurrency)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="text.secondary">
                                {/* ✅ YENİ: InventoryItems'da quantity/unit yok */}
                                -
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    component="div"
                    count={filteredAvailableProducts.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]}
                    labelRowsPerPage={t('kioskProductsTab.pagination.rowsPerPage')}
                    labelDisplayedRows={({ from, to, count }) => t('kioskProductsTab.pagination.displayedRows', { from, to, count })}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setProductDialogOpen(false);
              setSelectedProductIds([]);
              setSearchTerm('');
              setCategoryFilter('all');
            }} 
            color="inherit"
          >
            {t('kioskProductsTab.actions.cancel')}
          </Button>
          <Button 
            onClick={handleAddSelectedProducts}
            variant="contained"
            disabled={selectedProductIds.length === 0}
            startIcon={<AddIcon />}
          >
            {selectedProductIds.length > 0 
              ? t('kioskProductsTab.actions.addProductsCount', { count: selectedProductIds.length })
              : t('kioskProductsTab.actions.addProduct')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ek Ürün Seçimi Dialog */}
      <Dialog 
        open={relatedProductDialogOpen} 
        onClose={() => {
          setRelatedProductDialogOpen(false);
          setSelectedRelatedProductIds([]);
          setCurrentProductForRelated(null);
          setSearchTerm('');
          setCategoryFilter('all');
        }} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">
                {t('kioskProductsTab.dialogs.relatedProducts.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedRelatedProductIds.length > 0 && t('kioskProductsTab.dialogs.addProduct.selectedCount', { count: selectedRelatedProductIds.length })}
              </Typography>
            </Box>
            <IconButton 
              onClick={() => {
                setRelatedProductDialogOpen(false);
                setSelectedRelatedProductIds([]);
                setCurrentProductForRelated(null);
                setSearchTerm('');
                setCategoryFilter('all');
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingProducts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Filtreleme ve Arama */}
              <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder={t('kioskProductsTab.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ flex: 1, minWidth: 250 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>{t('kioskProductsTab.categoryFilter')}</InputLabel>
                  <Select
                    value={categoryFilter}
                    label={t('kioskProductsTab.categoryFilter')}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <MenuItem value="all">{t('kioskProductsTab.allCategories')}</MenuItem>
                    {Array.from(new Set(availableProducts.map(p => p.categoryName))).filter(Boolean).map(cat => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {filteredAvailableProducts.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <ShoppingCartIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    {t('kioskProductsTab.emptyState.productNotFound')}
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={selectedRelatedProductIds.length > 0 && selectedRelatedProductIds.length < paginatedProducts.length}
                              checked={paginatedProducts.length > 0 && selectedRelatedProductIds.length === paginatedProducts.length}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedRelatedProductIds(paginatedProducts.map(p => p.id));
                                } else {
                                  setSelectedRelatedProductIds([]);
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('kioskProductsTab.tableHeaders.product')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('kioskProductsTab.tableHeaders.category')}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{t('kioskProductsTab.tableHeaders.stockCode')}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('kioskProductsTab.tableHeaders.price')}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{t('kioskProductsTab.tableHeaders.quantity')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedProducts.map((product) => {
                          // Ana ürünü hariç tut
                          if (product.id === currentProductForRelated?.kioskProductId) return null;
                          
                          return (
                            <TableRow
                              key={product.id}
                              hover
                              onClick={() => {
                                if (selectedRelatedProductIds.includes(product.id)) {
                                  setSelectedRelatedProductIds(selectedRelatedProductIds.filter(id => id !== product.id));
                                } else {
                                  setSelectedRelatedProductIds([...selectedRelatedProductIds, product.id]);
                                }
                              }}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={selectedRelatedProductIds.includes(product.id)}
                                  onChange={() => {
                                    if (selectedRelatedProductIds.includes(product.id)) {
                                      setSelectedRelatedProductIds(selectedRelatedProductIds.filter(id => id !== product.id));
                                    } else {
                                      setSelectedRelatedProductIds([...selectedRelatedProductIds, product.id]);
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  {product.imageUrl ? (
                                    <Avatar
                                      src={product.imageUrl}
                                      alt={product.name}
                                      variant="rounded"
                                      sx={{ width: 40, height: 40 }}
                                    />
                                  ) : (
                                    <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: 'grey.300' }}>
                                      <ImageIcon />
                                    </Avatar>
                                  )}
                                  <Typography variant="body2">{product.name}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{product.categoryName || '-'}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{product.stockCode || '-'}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="medium" color="success.main">
                                  {product.salePrice.toLocaleString(numberLocale, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}{' '}
                                  {getCurrencySymbol(product.saleCurrency)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" color="text.secondary">
                                  {/* ✅ YENİ: InventoryItems'da quantity/unit yok */}
                                  -
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    component="div"
                    count={filteredAvailableProducts.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]}
                    labelRowsPerPage={t('kioskProductsTab.pagination.rowsPerPage')}
                    labelDisplayedRows={({ from, to, count }) => t('kioskProductsTab.pagination.displayedRows', { from, to, count })}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setRelatedProductDialogOpen(false);
              setSelectedRelatedProductIds([]);
              setCurrentProductForRelated(null);
              setSearchTerm('');
              setCategoryFilter('all');
            }} 
            color="inherit"
          >
            {t('kioskProductsTab.actions.cancel')}
          </Button>
          <Button 
            onClick={() => {
              if (currentProductForRelated) {
                const updatedProducts = { ...products };
                const categoryProducts = updatedProducts[currentProductForRelated.categoryId] || [];
                
                // Ana ürünü bul ve relatedProductIds'i güncelle
                const updatedCategoryProducts = categoryProducts.map(p => 
                  p.kioskProductId === currentProductForRelated.kioskProductId
                    ? { ...p, relatedProductIds: selectedRelatedProductIds }
                    : p
                );
                
                updatedProducts[currentProductForRelated.categoryId] = updatedCategoryProducts;
                onProductsChange(updatedProducts);
                
                console.log('✅ [KIOSK PRODUCTS] Ek ürünler kaydedildi:', {
                  productId: currentProductForRelated.kioskProductId,
                  relatedIds: selectedRelatedProductIds
                });
              }
              
              setRelatedProductDialogOpen(false);
              setSelectedRelatedProductIds([]);
              setCurrentProductForRelated(null);
              setSearchTerm('');
              setCategoryFilter('all');
            }}
            variant="contained"
            startIcon={<LinkIcon />}
          >
            {selectedRelatedProductIds.length > 0 
              ? t('kioskProductsTab.actions.saveRelatedCount', { count: selectedRelatedProductIds.length })
              : t('kioskProductsTab.actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ YENİ: Ürün Özelleştirme Seçenekleri Dialog */}
      <Dialog
        open={customizationDialogOpen}
        onClose={() => {
          setCustomizationDialogOpen(false);
          setCustomizationOptions([]);
          setCurrentProductForCustomization(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {t('kioskProductsTab.dialogs.customization.title')}
            </Typography>
            <IconButton onClick={() => {
              setCustomizationDialogOpen(false);
              setCustomizationOptions([]);
              setCurrentProductForCustomization(null);
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {/* ✅ Yeni: Taslak Seçimi */}
          {savedTemplates.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('kioskProductsTab.templates.selectLabel')}</InputLabel>
                <Select
                  value={selectedTemplate}
                  label={t('kioskProductsTab.templates.selectLabel')}
                  onChange={(e) => {
                    const templateName = e.target.value;
                    if (templateName) {
                      applyTemplate(templateName);
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>{t('kioskProductsTab.templates.dontUse')}</em>
                  </MenuItem>
                  {savedTemplates.map((template) => (
                    <MenuItem key={template.name} value={template.name}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <Typography>{template.name}</Typography>
                        <Chip 
                          label={t('kioskProductsTab.templates.optionCount', { count: template.options.length })}
                          size="small" 
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
          
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setNewOptionDialogOpen(true)}
              fullWidth
            >
              {t('kioskProductsTab.actions.addCustomizationOption')}
            </Button>
          </Box>

          {customizationOptions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <TuneIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                {t('kioskProductsTab.dialogs.customization.emptyTitle')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('kioskProductsTab.dialogs.customization.emptyDescription')}
              </Typography>
            </Box>
          ) : (
            <List>
              {customizationOptions.map((option, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => {
                        setCustomizationOptions(customizationOptions.filter((_, i) => i !== index));
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText primary={option} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', px: 1 }}>
            {/* Sol taraf: Taslağı Kaydet */}
            <Button
              startIcon={<SaveIcon />}
              onClick={() => setSaveTemplateDialogOpen(true)}
              disabled={customizationOptions.length === 0}
              color="info"
            >
              {t('kioskProductsTab.actions.saveTemplate')}
            </Button>
            
            {/* Sağ taraf: İptal ve Kaydet */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={() => {
                setCustomizationDialogOpen(false);
                setCustomizationOptions([]);
                setCurrentProductForCustomization(null);
                setSelectedTemplate('');
              }}>
                {t('kioskProductsTab.actions.cancel')}
              </Button>
              <Button
                variant="contained"
                startIcon={<TuneIcon />}
                onClick={() => {
                  if (currentProductForCustomization) {
                    const updatedProducts = { ...products };
                    const categoryProducts = updatedProducts[currentProductForCustomization.categoryId] || [];

                    // Ana ürünü bul ve customizationOptions'ı güncelle
                    const updatedCategoryProducts = categoryProducts.map(p =>
                      p.kioskProductId === currentProductForCustomization.kioskProductId
                        ? { ...p, customizationOptions: customizationOptions }
                        : p
                    );

                    updatedProducts[currentProductForCustomization.categoryId] = updatedCategoryProducts;
                    onProductsChange(updatedProducts);

                    console.log('✅ [KIOSK PRODUCTS] Özelleştirme seçenekleri kaydedildi:', {
                      productId: currentProductForCustomization.kioskProductId,
                      options: customizationOptions
                    });
                  }

                  setCustomizationDialogOpen(false);
                  setCustomizationOptions([]);
                  setCurrentProductForCustomization(null);
                  setSelectedTemplate('');
                }}
              >
                {customizationOptions.length > 0
                  ? t('kioskProductsTab.actions.saveOptionsCount', { count: customizationOptions.length })
                  : t('kioskProductsTab.actions.save')}
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ✅ YENİ: Yeni Özelleştirme Seçeneği Ekle Dialog (Küçük Modal) */}
      <Dialog
        open={newOptionDialogOpen}
        onClose={() => {
          setNewOptionDialogOpen(false);
          setNewOptionText('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('kioskProductsTab.dialogs.newOption.title')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('kioskProductsTab.dialogs.newOption.label')}
            fullWidth
            variant="outlined"
            value={newOptionText}
            onChange={(e) => setNewOptionText(e.target.value)}
            placeholder={t('kioskProductsTab.dialogs.newOption.placeholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setNewOptionDialogOpen(false);
            setNewOptionText('');
          }}>
            {t('kioskProductsTab.actions.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (newOptionText.trim()) {
                setCustomizationOptions([...customizationOptions, newOptionText.trim()]);
                setNewOptionText('');
                setNewOptionDialogOpen(false);
              }
            }}
            disabled={!newOptionText.trim()}
          >
            {t('kioskProductsTab.actions.add')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* ✅ YENİ: Taslağı Kaydet Dialog */}
      <Dialog
        open={saveTemplateDialogOpen}
        onClose={() => {
          setSaveTemplateDialogOpen(false);
          setTemplateName('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('kioskProductsTab.dialogs.saveTemplate.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('kioskProductsTab.dialogs.saveTemplate.description')}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t('kioskProductsTab.dialogs.saveTemplate.label')}
            fullWidth
            variant="outlined"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder={t('kioskProductsTab.dialogs.saveTemplate.placeholder')}
            helperText={t('kioskProductsTab.dialogs.saveTemplate.helperText', { count: customizationOptions.length })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setSaveTemplateDialogOpen(false);
            setTemplateName('');
          }}>
            {t('kioskProductsTab.actions.cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => {
              if (templateName.trim() && customizationOptions.length > 0) {
                // Aynı isimde taslak var mı kontrol et
                const exists = savedTemplates.some(tpl => tpl.name === templateName.trim());
                if (exists) {
                  alert(t('kioskProductsTab.alerts.templateExists'));
                  return;
                }
                
                const success = saveTemplate(templateName.trim(), customizationOptions);
                if (success) {
                  setSaveTemplateDialogOpen(false);
                  setTemplateName('');
                  // Başarı mesajı göster
                  alert(t('kioskProductsTab.alerts.templateSaved', { name: templateName.trim() }));
                }
              }
            }}
            disabled={!templateName.trim() || customizationOptions.length === 0}
          >
            {t('kioskProductsTab.actions.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default KioskProductsTab;

